import React from 'react';
import {
  Alert,
  Box,
  Button,
  Divider,
  FormControl,
  FormControlLabel,
  FormLabel,
  Grid,
  IconButton,
  MenuItem,
  Paper,
  Radio,
  RadioGroup,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography
} from '@mui/material';
import { Add, DeleteOutline, PictureAsPdf } from '@mui/icons-material';
import { EMPLOYMENT_TYPES, FORM_REFS } from '../../constants/certificationAuthorization';
import AuthorizationAttachmentsSection from '../authorization/AuthorizationAttachmentsSection';
import RowAttachmentsCell from '../authorization/RowAttachmentsCell';
import { rowPendingKey } from '../../utils/authorizationAttachments';
import GetsBrandHeader from './GetsBrandHeader';

const emptyExperienceRow = () => ({
  aircraftType: '',
  natureOfWork: '',
  organisation: '',
  fromDate: '',
  toDate: '',
  attachments: []
});

const emptyTrainingRow = () => ({
  trainingType: '',
  duration: '',
  conductedBy: '',
  startDate: '',
  validity: '',
  attachments: []
});

export const emptyGetsCm008Form = (overrides = {}) => ({
  applicantName: '',
  applicationType: 'ISSUE',
  employeeType: 'PERMANENT',
  employeeId: '',
  designation: '',
  aircraftEngineTypesRequired: '',
  scopeOfAuthorizationSought: '',
  ratingsWithLimitations: '',
  qualification: {
    licenseNumber: '',
    licenseEndorsed: '',
    issuingAuthority: '',
    validity: ''
  },
  recentExperience: [emptyExperienceRow()],
  trainingDetails: [emptyTrainingRow()],
  applicantDeclarationDate: '',
  applicantDeclarationPlace: '',
  ...overrides
});

/**
 * GETS/CM/008 Page 1 — Application for Issue / Extension / Renewal of Certification Authorization
 */
function GetsCm008ApplicationForm({
  value,
  onChange,
  readOnly = false,
  showHeader = true,
  onDownloadPdf,
  attachments,
  pendingFiles,
  attachmentsSaving = false,
  onAddAttachmentFiles,
  onRemovePendingAttachment,
  onViewAttachment,
  onDownloadAttachment,
  onDeleteAttachment,
  sectionAttachments,
  pendingSectionFiles,
  onAddSectionFiles,
  onRemovePendingSectionFile,
  onDeleteSectionAttachment,
  pendingRowFiles,
  onAddRowFiles,
  onRemovePendingRowFile,
  onDeleteRowAttachment,
  onRemoveRowIndex,
  attachmentsReadOnly
}) {
  const form = value || emptyGetsCm008Form();
  const set = (patch) => onChange({ ...form, ...patch });
  const setQualification = (patch) =>
    set({ qualification: { ...(form.qualification || {}), ...patch } });
  const attsReadOnly =
    typeof attachmentsReadOnly === 'boolean' ? attachmentsReadOnly : readOnly;

  const experience = Array.isArray(form.recentExperience) && form.recentExperience.length
    ? form.recentExperience
    : [emptyExperienceRow()];
  const trainings = Array.isArray(form.trainingDetails) && form.trainingDetails.length
    ? form.trainingDetails
    : [emptyTrainingRow()];

  const updateExperience = (index, patch) => {
    const next = experience.map((row, i) => (i === index ? { ...row, ...patch } : row));
    set({ recentExperience: next });
  };
  const updateTraining = (index, patch) => {
    const next = trainings.map((row, i) => (i === index ? { ...row, ...patch } : row));
    set({ trainingDetails: next });
  };

  const removeExperienceRow = (index) => {
    set({ recentExperience: experience.filter((_, i) => i !== index) });
    onRemoveRowIndex?.('experience', index);
  };
  const removeTrainingRow = (index) => {
    set({ trainingDetails: trainings.filter((_, i) => i !== index) });
    onRemoveRowIndex?.('training', index);
  };

  const fieldProps = readOnly ? { InputProps: { readOnly: true } } : {};
  const sectionAtts = sectionAttachments || {};
  const pendingSections = pendingSectionFiles || {};
  const pendingRows = pendingRowFiles || {};
  const renderSectionAttachments = (section) => (
    <Box sx={{ mt: 1.5 }}>
      <AuthorizationAttachmentsSection
        title="Attachments"
        attachments={sectionAtts[section] || []}
        pendingFiles={pendingSections[section] || []}
        readOnly={attsReadOnly}
        saving={attachmentsSaving}
        onAddFiles={
          onAddSectionFiles ? (files) => onAddSectionFiles(section, files) : undefined
        }
        onRemovePending={
          onRemovePendingSectionFile
            ? (index) => onRemovePendingSectionFile(section, index)
            : undefined
        }
        onView={onViewAttachment}
        onDownload={onDownloadAttachment}
        onDelete={
          onDeleteSectionAttachment
            ? (att) => onDeleteSectionAttachment(section, att)
            : undefined
        }
      />
    </Box>
  );
  const renderRowAttachments = (section, row, index) => (
    <RowAttachmentsCell
      attachments={row.attachments || []}
      pendingFiles={pendingRows[rowPendingKey(section, index)] || []}
      readOnly={attsReadOnly}
      saving={attachmentsSaving}
      onAddFiles={
        onAddRowFiles ? (files) => onAddRowFiles(section, index, files, row) : undefined
      }
      onRemovePending={
        onRemovePendingRowFile
          ? (fileIndex) => onRemovePendingRowFile(section, index, fileIndex)
          : undefined
      }
      onView={onViewAttachment}
      onDownload={onDownloadAttachment}
      onDelete={
        onDeleteRowAttachment
          ? (att) => onDeleteRowAttachment(section, row, att)
          : undefined
      }
    />
  );

  return (
    <Paper
      variant="outlined"
      sx={{
        p: { xs: 1.5, sm: 2.5 },
        borderColor: 'text.primary',
        borderWidth: 2,
        borderStyle: 'double',
        bgcolor: 'background.paper'
      }}
    >
      {showHeader && (
        <Box sx={{ mb: 2 }}>
          <GetsBrandHeader
            title="Application for Issue / Extension / Renewal of Certification Authorization"
            subtitle={`Form: ${FORM_REFS.application} | Issue: 01 Rev 00 Dtd Jul 2026`}
          />
          {onDownloadPdf && (
            <Stack direction="row" justifyContent="flex-end" sx={{ mt: -1, mb: 1 }}>
              <Button size="small" variant="outlined" startIcon={<PictureAsPdf />} onClick={onDownloadPdf}>
                Download {FORM_REFS.application} PDF
              </Button>
            </Stack>
          )}
        </Box>
      )}

      {/* Header row — Name / Employee Type / Application for / Employee ID */}
      <Grid container spacing={1.5} sx={{ mb: 2 }}>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            size="small"
            required
            label="Name of the Applicant"
            value={form.applicantName || ''}
            onChange={(e) => set({ applicantName: e.target.value })}
            {...fieldProps}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            select={!readOnly}
            fullWidth
            size="small"
            label="Employee Type"
            value={form.employeeType || ''}
            onChange={(e) => set({ employeeType: e.target.value })}
            {...fieldProps}
          >
            {EMPLOYMENT_TYPES.map((t) => (
              <MenuItem key={t.value} value={t.value}>
                {t.label}
              </MenuItem>
            ))}
          </TextField>
        </Grid>
        <Grid item xs={12} sm={8}>
          <FormControl component="fieldset" disabled={readOnly} sx={{ width: '100%' }}>
            <FormLabel component="legend" sx={{ typography: 'body2', mb: 0.5 }}>
              Application for
            </FormLabel>
            <RadioGroup
              row
              value={form.applicationType || 'ISSUE'}
              onChange={(e) => set({ applicationType: e.target.value })}
            >
              <FormControlLabel value="ISSUE" control={<Radio size="small" />} label="Issue" />
              <FormControlLabel value="EXTENSION" control={<Radio size="small" />} label="Extension" />
              <FormControlLabel value="RENEWAL" control={<Radio size="small" />} label="Renewal" />
            </RadioGroup>
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={4}>
          <TextField
            fullWidth
            size="small"
            label="Employee ID"
            value={form.employeeId || ''}
            onChange={(e) => set({ employeeId: e.target.value })}
            {...fieldProps}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            size="small"
            multiline
            minRows={2}
            label="Approval on Aircraft & Engine Type Required"
            value={form.aircraftEngineTypesRequired || ''}
            onChange={(e) => set({ aircraftEngineTypesRequired: e.target.value })}
            {...fieldProps}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            size="small"
            multiline
            minRows={2}
            label="Scope of"
            helperText="Scope of authorization sought"
            value={form.scopeOfAuthorizationSought || ''}
            onChange={(e) => set({ scopeOfAuthorizationSought: e.target.value })}
            {...fieldProps}
          />
        </Grid>
      </Grid>

      {/* QUALIFICATION DETAILS */}
      <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 1.5, mb: 2 }}>
        <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1, textTransform: 'uppercase' }}>
          Qualification Details
        </Typography>
        <TextField
          fullWidth
          size="small"
          multiline
          minRows={2}
          label="Rating(s) with Limitations"
          value={form.ratingsWithLimitations || ''}
          onChange={(e) => set({ ratingsWithLimitations: e.target.value })}
          sx={{ mb: 1.5 }}
          {...fieldProps}
        />
        <Grid container spacing={1.5}>
          <Grid item xs={12} sm={3}>
            <TextField
              fullWidth
              size="small"
              label="AME License No. / BAMEL / BAMEEC"
              value={form.qualification?.licenseNumber || ''}
              onChange={(e) => setQualification({ licenseNumber: e.target.value })}
              {...fieldProps}
            />
          </Grid>
          <Grid item xs={12} sm={3}>
            <TextField
              fullWidth
              size="small"
              label="License Endorsed"
              value={form.qualification?.licenseEndorsed || ''}
              onChange={(e) => setQualification({ licenseEndorsed: e.target.value })}
              {...fieldProps}
            />
          </Grid>
          <Grid item xs={12} sm={3}>
            <TextField
              fullWidth
              size="small"
              label="Issuing Authority"
              value={form.qualification?.issuingAuthority || ''}
              onChange={(e) => setQualification({ issuingAuthority: e.target.value })}
              {...fieldProps}
            />
          </Grid>
          <Grid item xs={12} sm={3}>
            <TextField
              fullWidth
              size="small"
              type="date"
              label="Validity"
              InputLabelProps={{ shrink: true }}
              value={form.qualification?.validity ? String(form.qualification.validity).slice(0, 10) : ''}
              onChange={(e) => setQualification({ validity: e.target.value })}
              {...fieldProps}
            />
          </Grid>
        </Grid>
        <Alert severity="info" sx={{ mt: 1.5, py: 0 }}>
          * Attach AME License copy / BAMEL / BAMEEC / Basic Knowledge Examination Certificate as applicable.
        </Alert>
        {renderSectionAttachments('qualification')}
      </Box>

      {/* RECENT EXPERIENCE */}
      <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 1.5, mb: 2 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
          <Typography variant="subtitle2" fontWeight={700} sx={{ textTransform: 'uppercase' }}>
            Recent Experience (2 Years) Details
          </Typography>
          {!readOnly && (
            <Button
              size="small"
              startIcon={<Add />}
              onClick={() => set({ recentExperience: [...experience, emptyExperienceRow()] })}
            >
              Add row
            </Button>
          )}
        </Stack>
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
          * Attach Logbook Copies
        </Typography>
        <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Type of Aircraft</TableCell>
                <TableCell>Nature of Work</TableCell>
                <TableCell>Organisation</TableCell>
                <TableCell>From</TableCell>
                <TableCell>To</TableCell>
                <TableCell>Attachments</TableCell>
                {!readOnly && <TableCell width={48} />}
              </TableRow>
            </TableHead>
            <TableBody>
              {experience.map((row, index) => (
                <TableRow key={row._id || index}>
                  <TableCell>
                    <TextField
                      fullWidth
                      size="small"
                      variant="standard"
                      value={row.aircraftType || ''}
                      onChange={(e) => updateExperience(index, { aircraftType: e.target.value })}
                      {...fieldProps}
                    />
                  </TableCell>
                  <TableCell>
                    <TextField
                      fullWidth
                      size="small"
                      variant="standard"
                      value={row.natureOfWork || ''}
                      onChange={(e) => updateExperience(index, { natureOfWork: e.target.value })}
                      {...fieldProps}
                    />
                  </TableCell>
                  <TableCell>
                    <TextField
                      fullWidth
                      size="small"
                      variant="standard"
                      value={row.organisation || ''}
                      onChange={(e) => updateExperience(index, { organisation: e.target.value })}
                      {...fieldProps}
                    />
                  </TableCell>
                  <TableCell>
                    <TextField
                      fullWidth
                      size="small"
                      type="date"
                      variant="standard"
                      InputLabelProps={{ shrink: true }}
                      value={row.fromDate ? String(row.fromDate).slice(0, 10) : ''}
                      onChange={(e) => updateExperience(index, { fromDate: e.target.value })}
                      {...fieldProps}
                    />
                  </TableCell>
                  <TableCell>
                    <TextField
                      fullWidth
                      size="small"
                      type="date"
                      variant="standard"
                      InputLabelProps={{ shrink: true }}
                      value={row.toDate ? String(row.toDate).slice(0, 10) : ''}
                      onChange={(e) => updateExperience(index, { toDate: e.target.value })}
                      {...fieldProps}
                    />
                  </TableCell>
                  <TableCell>{renderRowAttachments('experience', row, index)}
                  </TableCell>
                  {!readOnly && (
                    <TableCell>
                      <IconButton
                        size="small"
                        disabled={experience.length <= 1}
                        onClick={() => removeExperienceRow(index)}
                      >
                        <DeleteOutline fontSize="small" />
                      </IconButton>
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
      </Box>

      {/* TRAINING DETAILS */}
      <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 1.5, mb: 2 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
          <Box>
            <Typography variant="subtitle2" fontWeight={700} sx={{ textTransform: 'uppercase' }}>
              Training Details
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Enclose training certificates — General, HF, SMS, CAR & Aircraft-Specific
            </Typography>
          </Box>
          {!readOnly && (
            <Button
              size="small"
              startIcon={<Add />}
              onClick={() => set({ trainingDetails: [...trainings, emptyTrainingRow()] })}
            >
              Add row
            </Button>
          )}
        </Stack>
        <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Type of Training Undergone</TableCell>
                <TableCell>Duration</TableCell>
                <TableCell>Conducted by</TableCell>
                <TableCell>Start Date</TableCell>
                <TableCell>Validity</TableCell>
                <TableCell>Attachments</TableCell>
                {!readOnly && <TableCell width={48} />}
              </TableRow>
            </TableHead>
            <TableBody>
              {trainings.map((row, index) => (
                <TableRow key={row._id || index}>
                  <TableCell>
                    <TextField
                      fullWidth
                      size="small"
                      variant="standard"
                      value={row.trainingType || ''}
                      onChange={(e) => updateTraining(index, { trainingType: e.target.value })}
                      {...fieldProps}
                    />
                  </TableCell>
                  <TableCell>
                    <TextField
                      fullWidth
                      size="small"
                      variant="standard"
                      value={row.duration || ''}
                      onChange={(e) => updateTraining(index, { duration: e.target.value })}
                      {...fieldProps}
                    />
                  </TableCell>
                  <TableCell>
                    <TextField
                      fullWidth
                      size="small"
                      variant="standard"
                      value={row.conductedBy || ''}
                      onChange={(e) => updateTraining(index, { conductedBy: e.target.value })}
                      {...fieldProps}
                    />
                  </TableCell>
                  <TableCell>
                    <TextField
                      fullWidth
                      size="small"
                      type="date"
                      variant="standard"
                      InputLabelProps={{ shrink: true }}
                      value={row.startDate ? String(row.startDate).slice(0, 10) : ''}
                      onChange={(e) => updateTraining(index, { startDate: e.target.value })}
                      {...fieldProps}
                    />
                  </TableCell>
                  <TableCell>
                    <TextField
                      fullWidth
                      size="small"
                      type="date"
                      variant="standard"
                      InputLabelProps={{ shrink: true }}
                      value={row.validity ? String(row.validity).slice(0, 10) : ''}
                      onChange={(e) => updateTraining(index, { validity: e.target.value })}
                      {...fieldProps}
                    />
                  </TableCell>
                  <TableCell>{renderRowAttachments('training', row, index)}
                  </TableCell>
                  {!readOnly && (
                    <TableCell>
                      <IconButton
                        size="small"
                        disabled={trainings.length <= 1}
                        onClick={() => removeTrainingRow(index)}
                      >
                        <DeleteOutline fontSize="small" />
                      </IconButton>
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
      </Box>

      <Box sx={{ mb: 2 }}>
        <AuthorizationAttachmentsSection
          title="Other attachments"
          attachments={
            Array.isArray(attachments)
              ? attachments
              : Array.isArray(form.applicationAttachments)
                ? form.applicationAttachments
                : []
          }
          pendingFiles={pendingFiles}
          readOnly={attsReadOnly}
          saving={attachmentsSaving}
          onAddFiles={onAddAttachmentFiles}
          onRemovePending={onRemovePendingAttachment}
          onView={onViewAttachment}
          onDownload={onDownloadAttachment}
          onDelete={onDeleteAttachment}
        />
      </Box>

      {/* Declaration */}
      <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 1.5 }}>
        <Typography variant="body2" sx={{ mb: 2, fontStyle: 'italic' }}>
          I hereby certify that the details given above are accurate, complete and current and I am
          conversant with organization policy and procedures.
        </Typography>
        <Divider sx={{ mb: 2 }} />
        <Grid container spacing={1.5}>
          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              size="small"
              label="Sign. of Applicant"
              value={form.applicantName || ''}
              helperText="Name as signature acknowledgement"
              disabled
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              size="small"
              type="date"
              label="Date"
              InputLabelProps={{ shrink: true }}
              value={form.applicantDeclarationDate ? String(form.applicantDeclarationDate).slice(0, 10) : ''}
              onChange={(e) => set({ applicantDeclarationDate: e.target.value })}
              {...fieldProps}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              size="small"
              label="Place"
              value={form.applicantDeclarationPlace || ''}
              onChange={(e) => set({ applicantDeclarationPlace: e.target.value })}
              {...fieldProps}
            />
          </Grid>
        </Grid>
      </Box>
    </Paper>
  );
}

export default GetsCm008ApplicationForm;
